/**
 * ContactNotes — add/edit/delete timestamped notes for a contact.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getContrastRatio } from '@mui/material/styles';
const useStyles = makeStyles()(theme => ({
    container: {
        overflowWrap: 'anywhere',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    noteItem: {
        padding: '10px 12px',
        borderRadius: '8px',
        backgroundColor: theme.palette.ui02,
        border: `1px solid ${theme.palette.ui03}`
    },
    noteContent: {
        fontSize: '14px',
        color: theme.palette.text01,
        marginBottom: '6px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    noteMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '11px',
        color: theme.palette.text02
    },
    noteActions: {
        display: 'flex',
        gap: '6px'
    },
    noteActionBtn: {
        background: 'none',
        border: 'none',
        color: theme.palette.text02,
        cursor: 'pointer',
        padding: '6px 10px',
        minHeight: 44,
        borderRadius: '4px',
        fontSize: '11px',
        '&:hover': {
            backgroundColor: theme.palette.ui03,
            color: theme.palette.text01
        }
    },
    deleteNoteBtn: {
        color: getContrastRatio(theme.palette.textError, theme.palette.ui02) >= 4.5 ? theme.palette.textError : theme.palette.text01
    },
    addRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '4px'
    },
    addInput: {
        flex: '1 1 170px',
        minWidth: 0,
        padding: '8px 10px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02,
        color: theme.palette.text01,
        fontSize: '13px',
        outline: 'none',
        resize: 'none',
        '&:focus': {
            borderColor: theme.palette.action01
        }
    },
    addBtn: {
        padding: '8px 14px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        '&:hover': { opacity: 0.9 },
        '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
    },
    editArea: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px 10px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.action01}`,
        backgroundColor: theme.palette.ui02,
        color: theme.palette.text01,
        fontSize: '13px',
        outline: 'none',
        resize: 'none',
        marginBottom: '4px'
    },
    editBtnRow: {
        display: 'flex',
        gap: '6px',
        justifyContent: 'flex-end'
    },
    editBtn: {
        padding: '6px 10px',
        minHeight: 44,
        borderRadius: '4px',
        border: `1px solid ${theme.palette.ui03}`,
        background: 'none',
        color: theme.palette.text01,
        fontSize: '12px',
        cursor: 'pointer',
        '&:hover': { backgroundColor: theme.palette.ui03 }
    },
    saveEditBtn: {
        padding: '6px 10px',
        minHeight: 44,
        borderRadius: '4px',
        border: 'none',
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        fontSize: '12px',
        cursor: 'pointer',
        '&:hover': { opacity: 0.9 }
    },
    emptyNotes: {
        color: theme.palette.text03,
        fontSize: '13px',
        padding: '4px 0'
    }
}));
function formatRelativeTime(timestamp) {
    const now = Date.now();
    const then = new Date(timestamp).getTime();
    const diffMins = Math.floor((now - then) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffMins / 1440);
    if (diffMins < 1)
        return 'Just now';
    if (diffMins < 60)
        return `${diffMins}m ago`;
    if (diffHours < 24)
        return `${diffHours}h ago`;
    if (diffDays < 7)
        return `${diffDays}d ago`;
    return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
export default function ContactNotes({ notes, onAdd, onUpdate, onDelete, onRefresh, readOnly = false }) {
    const { classes, cx } = useStyles();
    const [newContent, setNewContent] = useState('');
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [needsReview, setNeedsReview] = useState(false);
    const pending = useRef(false), active = useRef(true), outcomeVersion = useRef(0);
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;
    const version = outcomeVersion.current;
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const disabled = busy || readOnly || needsReview;
    const mutate = useCallback(async (work, confirmed) => {
        if (!active.current || pending.current || readOnlyRef.current || needsReview || version !== outcomeVersion.current)
            return;
        pending.current = true;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await work();
            if (active.current)
                confirmed();
        }
        catch (failure) {
            if (active.current) {
                setError(failure instanceof Error ? failure.message : 'The note change could not be confirmed.');
                setNeedsReview(true);
            }
        }
        finally {
            outcomeVersion.current++;
            pending.current = false;
            if (active.current)
                setBusy(false);
        }
    }, [readOnly, needsReview, version]);
    const handleAdd = useCallback(async () => {
        if (!newContent.trim())
            return;
        await mutate(() => onAdd(newContent.trim()), () => { setNewContent(''); setNotice('Note added.'); });
    }, [newContent, mutate, onAdd]);
    const startEdit = useCallback((note) => {
        if (pending.current || readOnlyRef.current || needsReview)
            return;
        setEditingId(note.id);
        setEditContent(note.content);
        setNotice('');
    }, [readOnly, needsReview]);
    const cancelEdit = useCallback(() => {
        if (pending.current)
            return;
        setEditingId(null);
        setEditContent('');
    }, []);
    const saveEdit = useCallback(async () => {
        if (!editingId || !editContent.trim())
            return;
        await mutate(() => onUpdate(editingId, editContent.trim()), () => { setEditingId(null); setEditContent(''); setNotice('Note updated.'); });
    }, [editingId, editContent, mutate, onUpdate]);
    const handleDelete = useCallback(async (noteId) => {
        await mutate(() => onDelete(noteId), () => { setNotice('Note deleted.'); });
    }, [mutate, onDelete]);
    const refresh = useCallback(async () => {
        if (!active.current || pending.current)
            return;
        pending.current = true;
        setBusy(true);
        try {
            const confirmed = await onRefresh();
            if (active.current && confirmed) {
                setError('');
                setNeedsReview(false);
                setNotice('Notes refreshed. Review the saved notes before submitting your draft again.');
            }
        }
        catch {
            if (active.current)
                setError('Notes could not be refreshed. Your draft is kept. Try again.');
        }
        finally {
            outcomeVersion.current++;
            pending.current = false;
            if (active.current)
                setBusy(false);
        }
    }, [onRefresh]);
    return (React.createElement("div", { "aria-busy": busy, className: classes.container },
        error && React.createElement("div", { role: 'alert' }, error),
        notice && React.createElement("div", { "aria-live": 'polite', role: 'status' }, notice),
        needsReview && (React.createElement("div", null,
            React.createElement("p", null, "The change may already be saved. Refresh and review the notes before trying again. Your draft is kept."),
            React.createElement("button", { className: classes.editBtn, disabled: busy, onClick: refresh, type: 'button' }, "Refresh notes"))),
        !readOnly && !needsReview && notes.length === 0 && (React.createElement("div", { className: classes.emptyNotes }, "No notes yet.")),
        notes.map(note => (React.createElement("div", { className: classes.noteItem, key: note.id }, editingId === note.id ? (React.createElement(React.Fragment, null,
            React.createElement("textarea", { "aria-label": 'Edit note', disabled: disabled, className: classes.editArea, onChange: e => { if (!pending.current && !readOnly && !needsReview)
                    setEditContent(e.target.value ?? ''); }, rows: 3, value: editContent }),
            React.createElement("div", { className: classes.editBtnRow },
                React.createElement("button", { className: classes.editBtn, disabled: busy, onClick: cancelEdit, type: 'button' }, "Cancel"),
                React.createElement("button", { className: classes.saveEditBtn, disabled: disabled || !editContent.trim(), onClick: saveEdit, type: 'button' }, "Save")))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.noteContent }, note.content),
            React.createElement("div", { className: classes.noteMeta },
                React.createElement("span", null, formatRelativeTime(note.updated_at || note.created_at)),
                React.createElement("div", { className: classes.noteActions },
                    React.createElement("button", { className: classes.noteActionBtn, disabled: disabled, onClick: () => startEdit(note), type: 'button' }, "Edit"),
                    React.createElement("button", { className: cx(classes.noteActionBtn, classes.deleteNoteBtn), disabled: disabled, onClick: () => handleDelete(note.id), type: 'button' }, "Delete")))))))),
        React.createElement("div", { className: classes.addRow },
            React.createElement("textarea", { className: classes.addInput, "aria-label": 'Add a note', disabled: disabled, onChange: e => { if (!pending.current && !readOnly && !needsReview)
                    setNewContent(e.target.value ?? ''); }, onKeyDown: e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleAdd();
                    }
                }, placeholder: 'Add a note...', rows: 2, value: newContent }),
            React.createElement("button", { className: classes.addBtn, disabled: disabled || !newContent.trim(), onClick: handleAdd, type: 'button' }, busy ? 'Saving…' : 'Add'))));
}
